import React, { useState } from 'react';
import { Play, Youtube } from 'lucide-react';
import { getLessonVideo } from '../data/lessonVideos';

const VideoPlayer = ({ moduleId, lessonId, title, duration, poster }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  
  // Get video data for this lesson
  const videoData = getLessonVideo(moduleId, lessonId);
  
  // If we have a YouTube video, render the embed
  if (videoData?.youtubeId) {
    return (
      <div className="relative w-full rounded-xl overflow-hidden aspect-video mb-6 shadow-lg bg-slate-900">
        {!isLoaded ? (
          // Thumbnail with play button (lazy load iframe)
          <div 
            className="absolute inset-0 cursor-pointer group"
            onClick={() => setIsLoaded(true)}
          >
            {/* YouTube thumbnail */}
            <img 
              src={`https://img.youtube.com/vi/${videoData.youtubeId}/maxresdefault.jpg`}
              alt={videoData.title || title}
              className="w-full h-full object-cover"
              onError={(e) => {
                // Fallback to lower quality thumbnail if maxres not available
                e.target.src = `https://img.youtube.com/vi/${videoData.youtubeId}/hqdefault.jpg`;
              }}
            />
            
            {/* Overlay */}
            <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors" />
            
            {/* Play button */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="w-20 h-20 rounded-full bg-red-600 flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-xl">
                <Play className="w-8 h-8 text-white ml-1" fill="white" />
              </div>
              <p className="mt-4 text-white font-medium text-lg">{videoData.title || title}</p>
              <p className="text-white/70 text-sm mt-1 flex items-center gap-2">
                <Youtube className="w-4 h-4" />
                {videoData.duration || duration}
              </p>
            </div>
          </div>
        ) : (
          // Actual YouTube iframe
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube.com/embed/${videoData.youtubeId}?autoplay=1&rel=0`}
            title={videoData.title || title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        )}
      </div>
    );
  }

  // Fallback placeholder for lessons without videos
  return (
    <div className="relative w-full rounded-xl overflow-hidden bg-gradient-to-br from-slate-800 to-slate-900 aspect-video group cursor-pointer mb-6">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
          backgroundSize: '20px 20px'
        }} />
      </div>
      
      {/* Poster image overlay */}
      {poster && (
        <img 
          src={poster} 
          alt={title}
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
      )}
      
      {/* Content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-6">
        {/* Play button */}
        <div className="w-20 h-20 rounded-full bg-emerald-500/90 flex items-center justify-center mb-4 transform group-hover:scale-110 transition-transform shadow-lg shadow-emerald-500/30">
          <Play className="w-8 h-8 text-white ml-1" fill="white" />
        </div>
        
        <h4 className="text-lg font-semibold text-center mb-2">{title || 'Video Lesson'}</h4>
        {duration && (
          <p className="text-sm text-slate-400">Duration: {duration}</p>
        )}
        
        <div className="mt-4 px-4 py-2 bg-white/10 rounded-full text-sm backdrop-blur-sm">
          Video coming soon
        </div>
      </div>
      
      {/* Bottom progress bar placeholder */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-slate-700">
        <div className="h-full bg-emerald-500 w-0" />
      </div>
    </div>
  );
};

// YouTube embed component for direct use
export const YouTubeEmbed = ({ videoId, title }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  
  if (!videoId) return null;
  
  return (
    <div className="relative w-full rounded-xl overflow-hidden aspect-video mb-6 shadow-lg">
      {!isLoaded ? (
        <div 
          className="absolute inset-0 cursor-pointer group bg-slate-900"
          onClick={() => setIsLoaded(true)}
        >
          <img 
            src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
            alt={title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
            }}
          />
          <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center transform group-hover:scale-110 transition-transform">
              <Play className="w-6 h-6 text-white ml-1" fill="white" />
            </div>
          </div>
        </div>
      ) : (
        <iframe
          className="absolute inset-0 w-full h-full"
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
          title={title || 'Video lesson'}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      )}
    </div>
  );
};

export default VideoPlayer;
